import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'react-hot-toast';

// =====================================================
// INTERFACES
// =====================================================

interface DriverApplication {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  address: string;
  city: string;
  license_number: string;
  license_expiry_date: string;
  license_type: string;
  years_driving_experience: number;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'kyc_required';
  application_date: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  rejection_reason?: string;
  kyc_documents_count?: number;
  profiles?: {
    id: string;
    full_name: string;
    email: string;
    phone: string;
    created_at: string;
  };
  kyc_documents?: KYCDocument[];
}

interface KYCDocument {
  id: string;
  document_type: 'id_document' | 'license_document' | 'proof_of_address';
  file_name: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  is_verified: boolean;
  verified_by?: string;
  verified_at?: string;
  verification_notes?: string;
  created_at: string;
}

// =====================================================
// ADMIN DRIVER APPLICATIONS MANAGEMENT COMPONENT
// =====================================================

const AdminDriverApplications: React.FC = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<DriverApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<DriverApplication | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchApplications();
    }
  }, [user, filterStatus]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/applications?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
      } else {
        toast.error('Failed to fetch driver applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch driver applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationDetails = async (applicationId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/applications/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedApplication(data.application);
        setIsModalOpen(true);
      } else {
        toast.error('Failed to fetch application details');
      }
    } catch (error) {
      console.error('Error fetching application details:', error);
      toast.error('Failed to fetch application details');
    }
  };

  const handleApproveApplication = async (applicationId: string, reviewNotes?: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/applications/${applicationId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reviewNotes: reviewNotes || null
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Application approved successfully! User ${data.userEmail} is now a driver.`);
        setIsModalOpen(false);
        fetchApplications();
      } else {
        toast.error(data.error || data.message || 'Failed to approve application');
      }
    } catch (error) {
      console.error('Error approving application:', error);
      toast.error('Failed to approve application');
    }
  };

  const handleRejectApplication = async (applicationId: string, rejectionReason: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/applications/${applicationId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rejectionReason
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Application rejected successfully');
        setIsModalOpen(false);
        fetchApplications();
      } else {
        toast.error(data.message || 'Failed to reject application');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error('Failed to reject application');
    }
  };

  const handleVerifyDocument = async (documentId: string, isVerified: boolean, verificationNotes?: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/admin/kyc/${documentId}/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isVerified,
          verificationNotes: verificationNotes || null
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Document ${isVerified ? 'verified' : 'rejected'} successfully`);
        // Refresh application details
        if (selectedApplication) {
          fetchApplicationDetails(selectedApplication.id);
        }
      } else {
        toast.error(data.message || 'Failed to verify document');
      }
    } catch (error) {
      console.error('Error verifying document:', error);
      toast.error('Failed to verify document');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'kyc_required':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    switch (type) {
      case 'id_document':
        return 'National ID/Passport';
      case 'license_document':
        return 'Driver\'s License';
      case 'proof_of_address':
        return 'Proof of Address';
      default:
        return type;
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.license_number.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading driver applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Manage Drivers</h1>
          <p className="mt-2 text-gray-600">Review and manage driver applications with KYC verification. Approve or reject applications after KYC documents are submitted.</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Applications</option>
                <option value="pending">Pending Review</option>
                <option value="kyc_required">KYC Required</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Applications
              </label>
              <input
                type="text"
                placeholder="Search by name, email, or license..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchApplications}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    License Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KYC Docs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {application.full_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.email}
                        </div>
                        <div className="text-sm text-gray-500">
                          {application.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {application.license_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          Type: {application.license_type}
                        </div>
                        <div className="text-sm text-gray-500">
                          Expires: {new Date(application.license_expiry_date).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(application.status)}`}>
                        {application.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <span className={(application.kyc_documents_count || 0) >= 3 ? 'text-green-600 font-semibold' : 'text-orange-600'}>
                          {application.kyc_documents_count || 0}/3
                        </span>
                        {(application.kyc_documents_count || 0) >= 3 && (
                          <span className="ml-2 text-green-600" title="All KYC documents submitted">✓</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(application.application_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => fetchApplicationDetails(application.id)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredApplications.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No driver applications found.</p>
            </div>
          )}
        </div>

        {/* Application Details Modal */}
        {isModalOpen && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Driver Application Review
                  </h3>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>

                {/* Application Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">Personal Information</h4>
                    <div className="space-y-2">
                      <p><span className="font-medium">Name:</span> {selectedApplication.full_name}</p>
                      <p><span className="font-medium">Email:</span> {selectedApplication.email}</p>
                      <p><span className="font-medium">Phone:</span> {selectedApplication.phone}</p>
                      <p><span className="font-medium">Date of Birth:</span> {new Date(selectedApplication.date_of_birth).toLocaleDateString()}</p>
                      <p><span className="font-medium">Address:</span> {selectedApplication.address}</p>
                      <p><span className="font-medium">City:</span> {selectedApplication.city}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">License Information</h4>
                    <div className="space-y-2">
                      <p><span className="font-medium">License Number:</span> {selectedApplication.license_number}</p>
                      <p><span className="font-medium">License Type:</span> {selectedApplication.license_type}</p>
                      <p><span className="font-medium">Expiry Date:</span> {new Date(selectedApplication.license_expiry_date).toLocaleDateString()}</p>
                      <p><span className="font-medium">Experience:</span> {selectedApplication.years_driving_experience} years</p>
                    </div>
                  </div>
                </div>

                {/* KYC Documents */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-medium text-gray-900">KYC Documents</h4>
                    <span className={`text-sm font-medium ${
                      (selectedApplication.kyc_documents?.length || 0) >= 3
                        ? 'text-green-600'
                        : 'text-orange-600'
                    }`}>
                      {(selectedApplication.kyc_documents?.length || 0)}/3 submitted
                    </span>
                  </div>

                  {/* Missing Documents Warning */}
                  {(selectedApplication.kyc_documents?.length || 0) < 3 && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-700">
                        <strong>Missing Documents:</strong> {
                          ['id_document', 'license_document', 'proof_of_address']
                            .filter(type => !selectedApplication.kyc_documents?.some(doc => doc.document_type === type))
                            .map(type => getDocumentTypeLabel(type))
                            .join(', ')
                        }
                      </p>
                    </div>
                  )}

                  {selectedApplication.kyc_documents && selectedApplication.kyc_documents.length > 0 ? (
                    <div className="space-y-4">
                      {selectedApplication.kyc_documents.map((doc) => (
                        <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">
                                {getDocumentTypeLabel(doc.document_type)}
                              </h5>
                              <p className="text-sm text-gray-500">{doc.file_name}</p>
                              <p className="text-sm text-gray-500">
                                Uploaded: {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                              {doc.verification_notes && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <span className="font-medium">Notes:</span> {doc.verification_notes}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                doc.is_verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {doc.is_verified ? 'Verified' : 'Pending'}
                              </span>
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                View
                              </a>
                              {!doc.is_verified && (
                                <>
                                  <button
                                    onClick={() => handleVerifyDocument(doc.id, true)}
                                    className="text-green-600 hover:text-green-800 text-sm"
                                  >
                                    ✓ Verify
                                  </button>
                                  <button
                                    onClick={() => {
                                      const notes = prompt('Rejection reason:');
                                      if (notes) {
                                        handleVerifyDocument(doc.id, false, notes);
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    ✗ Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-gray-500">No KYC documents submitted yet.</p>
                    </div>
                  )}
                </div>

                {/* KYC Status Warning */}
                {selectedApplication.kyc_documents && selectedApplication.kyc_documents.length < 3 && (
                  <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start">
                      <span className="text-orange-600 text-xl mr-2">⚠️</span>
                      <div>
                        <h5 className="font-medium text-orange-900">KYC Documents Incomplete</h5>
                        <p className="text-sm text-orange-700 mt-1">
                          {selectedApplication.kyc_documents.length} of 3 required documents submitted.
                          All KYC documents must be submitted before approval.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      const reason = prompt('Rejection reason:');
                      if (reason) {
                        handleRejectApplication(selectedApplication.id, reason);
                      }
                    }}
                    disabled={selectedApplication.status === 'approved' || selectedApplication.status === 'rejected'}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reject Application
                  </button>
                  <button
                    onClick={() => {
                      const notes = prompt('Review notes (optional):');
                      handleApproveApplication(selectedApplication.id, notes || undefined);
                    }}
                    disabled={
                      selectedApplication.status === 'approved' ||
                      selectedApplication.status === 'rejected' ||
                      !selectedApplication.kyc_documents ||
                      selectedApplication.kyc_documents.length < 3 ||
                      (selectedApplication.status !== 'pending' && selectedApplication.status !== 'under_review')
                    }
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      !selectedApplication.kyc_documents || selectedApplication.kyc_documents.length < 3
                        ? 'All KYC documents must be submitted before approval'
                        : selectedApplication.status === 'approved'
                        ? 'Application already approved'
                        : selectedApplication.status === 'rejected'
                        ? 'Application already rejected'
                        : 'Approve application'
                    }
                  >
                    Approve Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDriverApplications;
